import React, { useState, useEffect, lazy, Suspense } from 'react'
import {
    BrowserRouter as Router,
    Routes,
    Route,
    useLocation,
} from 'react-router'
import { createGlobalStyle } from 'styled-components'
import { I18nextProvider } from 'react-i18next'
import { useTranslation } from 'react-i18next'
import './App.css'
import ErrorDisplay from '~/features/ui-components/error-display'
import { SimpleTabsLayout } from '~/features/ui-components/split-tabs'

import { AppProvider } from './app-context'
import type { CoreRendererProps } from './types'
import { GlobalModal } from './GlobalModal'
import { LoadingScreen } from '~common/components/loading-screen'
import { useCacheValue } from '~/cache-context'
import { CheckoutSuccessful } from '~/features/checkout-successful'
import { ThemeProvider } from '~common/theme/theme-provider'
import { Theme } from '~common/theme/types'
import { PostHogProvider } from 'posthog-js/react'
import { getEnvVar } from './features/config/service/config'
import { PostHogConfig } from 'posthog-js'
import { SidepanelProvider } from './features/sidepanel'
import { FileDropOverlay } from './features/file-drop-overlay'
import { Login } from './features/auth/ui/login'
import { QontoAuthCallback } from './features/plugins/ui/qonto-auth-callback'
import { i18n } from './i18n/config'
import { SCROLLBAR_WIDTH } from './constants'

// Lazy load heavy features
const InvoicesOverview = lazy(() =>
    import('~/features/invoices-overview').then((m) => ({
        default: m.InvoicesOverview,
    })),
)
const GmailSearch = lazy(() =>
    import('~/features/google-api/ui/gmail-search').then((m) => ({
        default: m.GmailSearch,
    })),
)
const TransactionsList = lazy(() =>
    import('./features/transactions').then((m) => ({
        default: m.TransactionsList,
    })),
)
const Portals = lazy(() =>
    import('./features/portals').then((m) => ({ default: m.Portals })),
)
const PluginsOverview = lazy(() =>
    import('./features/plugins/ui').then((m) => ({
        default: m.PluginsOverview,
    })),
)
const OnboardingFlow = lazy(() => import('./features/onboarding/ui'))

const GlobalStyle = createGlobalStyle`
    body {
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        padding: 0;
        font-family: ${({ theme }) => (theme as Theme).typography.fontFamily.sans};
        background-color: ${({ theme }) => (theme as Theme).colors.background.bg1};
        letter-spacing: 0.5px;
        color: ${({ theme }) => (theme as Theme).colors.text.primary};
        transition: all ${({ theme }) => (theme as Theme).transitions.normal};
        position: relative;
    }

    * {
        font-family: ${({ theme }) => (theme as Theme).typography.fontFamily.sans};
    }

    ::-webkit-scrollbar {
        width: ${SCROLLBAR_WIDTH}px;
        height: ${SCROLLBAR_WIDTH}px;
    }

    ::-webkit-scrollbar-track {
        background: ${({ theme }) => (theme as Theme).colors.background.bg1};
        border-radius: 10px;
    }

    ::-webkit-scrollbar-thumb {
        background: ${({ theme }) => (theme as Theme).colors.greyScale[100]};
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: ${({ theme }) => (theme as Theme).colors.greyScale[50]};
    }

    ::-webkit-scrollbar-corner {
        background: ${({ theme }) => (theme as Theme).colors.background.bg1};
    }
`

function InnerAppWithCache(props: CoreRendererProps) {
    const { t } = useTranslation('app')
    const [showOnboarding, setShowOnboarding] = useState<boolean>(false)
    const cacheLoadState = useCacheValue('loadState')
    const location = useLocation()

    // Early return for success route: bypass cache/onboarding, render directly
    if (location.pathname === '/checkout-successful') {
        return (
            <>
                <Routes>
                    <Route
                        path="/checkout-successful"
                        element={<CheckoutSuccessful />}
                    />
                </Routes>
                <ErrorDisplay services={props.services} />
                <GlobalModal
                    globalEvents={props.globalEvents!}
                    services={props.services}
                />
            </>
        )
    }

    if (location.pathname === '/qonto-auth') {
        return (
            <>
                <Routes>
                    <Route path="/qonto-auth" element={<QontoAuthCallback />} />
                </Routes>
            </>
        )
    }

    // Early return for login route: bypass cache/onboarding, render directly
    if (location.pathname === '/login') {
        return (
            <>
                <Routes>
                    <Route path="/login" element={<Login />} />
                </Routes>
                <ErrorDisplay services={props.services} />
                <GlobalModal
                    globalEvents={props.globalEvents!}
                    services={props.services}
                />
            </>
        )
    }

    useEffect(() => {
        const checkUser = async () => {
            const user = await props.services.auth.getLoggedInUser()
            if (!user) {
                setShowOnboarding(true)
            }
        }
        checkUser()
        props.services.extensionMessaging.connect()
        props.services.entitlements.getSubscriptionProduct()
        console.warn(
            '[App] Calling extensionMessaging.connect() from App.tsx useEffect',
        )
        return () => props.services.extensionMessaging.disconnect()
    }, [props.services.auth])

    // Show loading screen until cache is ready (for non-success routes)
    if (cacheLoadState === 'pristine' || cacheLoadState === 'running') {
        return <LoadingScreen message={t('loadingApplication')} />
    }

    // Show error screen if cache loading failed
    if (cacheLoadState === 'error') {
        return (
            <LoadingScreen
                message={t('failedToLoadApplication')}
                error={t('errorLoadingApplication')}
            />
        )
    }

    const isLogin = new URLSearchParams(location.search).has('login')

    // Cache is loaded successfully, render the main app
    return (
        <>
            <Suspense
                fallback={<LoadingScreen message={t('Preparing Tax Relief')} />}
            >
                <Routes>
                    <Route
                        path="/"
                        element={
                            <SimpleTabsLayout
                                services={props.services}
                                storageModules={props.storageModules}
                                initialTabId="invoices"
                                tabs={[
                                    {
                                        id: 'invoices',
                                        title: t('tabs.invoices'),
                                        render: () => <InvoicesOverview />,
                                        icon: 'document',
                                    },
                                    {
                                        id: 'gmail-search',
                                        title: t('tabs.emails'),
                                        render: () => <GmailSearch />,
                                        icon: 'mail',
                                    },
                                    {
                                        id: 'transactions',
                                        title: t('tabs.transactions'),
                                        render: () => <TransactionsList />,
                                        icon: 'bank',
                                    },
                                    {
                                        id: 'portals',
                                        title: 'Portals',
                                        render: () => <Portals />,
                                        icon: 'globe',
                                    },
                                    {
                                        id: 'plugins',
                                        title: t('tabs.plugins'),
                                        render: () => <PluginsOverview />,
                                        icon: 'puzzle',
                                    },
                                ]}
                            />
                        }
                    />
                </Routes>
            </Suspense>

            {showOnboarding && (
                <Suspense fallback={null}>
                    <OnboardingFlow
                        isOpen={showOnboarding}
                        onComplete={async (newUser) => {
                            setShowOnboarding(false)
                        }}
                        loginFlow={isLogin}
                    />
                </Suspense>
            )}
            <ErrorDisplay services={props.services} />
            <GlobalModal
                globalEvents={props.globalEvents!}
                services={props.services}
            />
        </>
    )
}

function AppWithCache(props: CoreRendererProps) {
    return (
        <Router>
            <InnerAppWithCache {...props} />
        </Router>
    )
}

export default function App(props: CoreRendererProps) {
    const PostHogOptions: Partial<PostHogConfig> = {
        api_host: getEnvVar('VITE_PUBLIC_POSTHOG_HOST', { isRequired: true }),
        defaults: '2025-05-24',
    }
    return (
        <I18nextProvider i18n={i18n}>
            <AppProvider
                services={props.services}
                storageModules={props.storageModules}
                globalEvents={props.globalEvents}
            >
                <PostHogProvider
                    apiKey={getEnvVar('VITE_PUBLIC_POSTHOG_KEY', {
                        isRequired: true,
                    })}
                    options={PostHogOptions}
                >
                    <ThemeProvider defaultTheme="dark">
                        <SidepanelProvider>
                            <GlobalStyle />
                            <FileDropOverlay />
                            <AppWithCache {...props} />
                        </SidepanelProvider>
                    </ThemeProvider>
                </PostHogProvider>
            </AppProvider>
        </I18nextProvider>
    )
}
